import {
  assetConditionSchema,
  assetMediaSchema,
  assetPricesPayloadSchema,
  buildingTypeSchema,
} from '@aikoa/common'
import { getCurrentLocale } from '@aikoa/i18n'
import { z } from 'zod/v4'

const locale = getCurrentLocale()
const currentYear = new Date().getFullYear()

export const propertyFilterFormObjectSchema = z.object({
  buildingTypes: z.array(buildingTypeSchema).min(1),
  conditions: z.array(assetConditionSchema).optional(),
  priceMin: z.number().min(locale.property.min_price).max(locale.property.max_price).optional(),
  priceMax: z.number().min(locale.property.min_price).max(locale.property.max_price),
  sizeMin: z.number().min(locale.property.min_size),
  sizeMax: z.number().max(locale.property.max_size).optional(),
  roomsMin: z.number().int().min(locale.property.min_rooms),
  roomsMax: z.number().int().max(locale.property.max_rooms).optional(),
  buildYearMin: z.string().optional(),
  buildYearMax: z.string().optional(),
})

export const propertyFilterFormSchema = propertyFilterFormObjectSchema
  .refine((d) => d.priceMin === undefined || d.priceMin <= d.priceMax, 'priceMin must be <= priceMax')
  .refine(
    (d) => d.sizeMin === undefined || d.sizeMax === undefined || d.sizeMin <= d.sizeMax,
    'sizeMin must be <= sizeMax',
  )
  .refine(
    (d) => d.roomsMin === undefined || d.roomsMax === undefined || d.roomsMin <= d.roomsMax,
    'roomsMin must be <= roomsMax',
  )

export type PropertyFilterFormData = z.infer<typeof propertyFilterFormObjectSchema>

export const propertyInputFormSchema = z.object({
  relationType: z.enum(['prospect', 'representation', 'reference']),
  address: z.object({
    street: z.string().min(1),
    street_number: z.string(),
    postal_code: z.string(),
    city: z.string(),
    municipality: z.string().nullish(),
    region: z.string().nullish(),
    state: z.string().nullish(),
    neighborhood: z.string().nullish(),
    // eslint-disable-next-line i18next/no-literal-string
    country: z.string().default('Finland'),
    unit_number: z.string().nullish(),
  }),
  location: z.object({
    lat: z.number(),
    lng: z.number(),
  }),
  buildingType: buildingTypeSchema,
  size: z.number().min(locale.property.min_size).max(locale.property.max_size),
  prices: assetPricesPayloadSchema,
  rooms: z.number().int().min(locale.property.min_rooms).max(locale.property.max_rooms).optional(),
  buildYear: z
    .number()
    .int()
    .min(locale.property.min_year)
    .max(currentYear)
    .optional(),
  media: z.array(assetMediaSchema).optional(),
  listingLink: z.string().url().optional().or(z.literal('')),
  notes: z.string().optional(),
})

export type PropertyInputData = z.infer<typeof propertyInputFormSchema>
